import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../context/LanguageContext';
import SEO from '../components/SEO';
import { SORTED_ARTICLES } from '../data/newsArticles';
import { CLIENT_LOGO_FILES_HOME } from '../data/clienteleLogos';
import '../styles/home.css';

const INDUSTRY_TILES = [
  { id: 'enterprise', image: '/Images/industries/office.jpg', path: 'enterprise-ai' },
  { id: 'government', image: '/Images/industries/government.jpg', path: 'ai-governance' },
  { id: 'manufacturing', image: '/Images/industries/manufacturing.jpg', path: 'industries' },
  { id: 'retail', image: '/Images/industries/retail.jpg', path: 'industries' },
  { id: 'healthcare', image: '/Images/industries/healthcare.jpg', path: 'industries' },
  { id: 'facilities', image: '/Images/industries/facilities.jpg', path: 'industries' },
];

const SERVICE_IMAGES = {
  knowledgeHub: '/Images/bg-intelligence.jpg',
  dataBridge: '/Images/bg-data-center.jpg',
  investigation: '/Images/home/home-service-investigation.jpg',
};

const OFFER_IMAGE_SOFTWARE = '/Images/custom-ai-system.jpg';
/** AI solutions card (existing hero-style photo) */
const OFFER_IMAGE_AI = '/Images/bg-command-center.jpg';
const TRACK_IMAGE = '/Images/home/home-track-record.jpg';
const WHY_IMAGE = '/Images/home/home-why-ewands.jpg';

function SectionImage({ src, alt }) {
  const [failed, setFailed] = useState(false);

  if (failed) {
    return (
      <div className="home-img-fallback" role="img" aria-label={alt}>
        {alt}
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      className="home-img"
      loading="lazy"
      decoding="async"
      onError={() => setFailed(true)}
    />
  );
}

const Home = () => {
  const { t } = useTranslation('home');
  const { t: tInd } = useTranslation('industries');
  const { language } = useLanguage();
  const lang = `/${language}`;
  const whyItems = t('why.items', { returnObjects: true });
  const newsItems = SORTED_ARTICLES.slice(0, 3);

  return (
    <div className="home-page">
      <SEO
        title={t('seo.title')}
        description={t('seo.description')}
        keywords={t('seo.keywords')}
        canonicalPath={`/${language}`}
        ogImage="/Images/logo.png"
      />

      <section className="hero-section">
        <div className="hero-background">
          <div className="gradient-layer" />
          <div className="particle-layer" />
          <div className="grid-overlay" />
          <div className="tech-lines" />
        </div>

        <div className="hero-content">
          <div className="container">
            <div className="hero-wrapper">
              <div className="tech-element tech-element-1" />
              <div className="tech-element tech-element-2" />
              <div className="tech-element tech-element-4" />
              <div className="tech-element tech-element-3 tech-interactive-dot" />

              <h1 className="hero-quote">
                <span className="quote-highlight">{t('hero.title')}</span>
                <br />
                {t('hero.subtitle1')}
                <br />
                {t('hero.subtitle2')}
              </h1>

              <Link to={`${lang}/platform`} className="hero-cta">
                <span className="cta-text-long">{t('hero.ctaLong')}</span>
                {'\u00A0'}
                {t('hero.cta')}
                <span className="cta-chevrons">
                  <span>›</span>
                  <span>›</span>
                  <span>›</span>
                </span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Track record */}
      <section className="home-section">
        <div className="container">
          <div className="home-track">
            <div>
              <span className="home-eyebrow">{t('trackRecord.eyebrow')}</span>
              <div className="home-track-years">
                <span className="home-track-years-num">{t('trackRecord.years')}</span>
                <span className="home-track-years-label">{t('trackRecord.yearsLabel')}</span>
              </div>
              <h2 className="home-h2">{t('trackRecord.title')}</h2>
              <p className="home-lead">{t('trackRecord.body')}</p>
              <p className="home-lead" style={{ fontSize: '0.95rem' }}>
                {t('trackRecord.line')}
              </p>
              <Link to={`${lang}/about`} className="home-link-arrow">
                {t('trackRecord.aboutLink')} →
              </Link>
            </div>
            <div className="home-img-wrap">
              <SectionImage src={TRACK_IMAGE} alt={t('trackRecord.imageAlt')} />
            </div>
          </div>
        </div>
      </section>

      {/* Clientele */}
      <section className="home-section home-section--muted">
        <div className="container home-logos">
          <span className="home-eyebrow">{t('clientele.eyebrow')}</span>
          <h2 className="home-h2" style={{ margin: '0 auto', textAlign: 'center', maxWidth: 'none' }}>
            {t('clientele.title')}
          </h2>
          <div className="home-logos-panel">
            <div className="home-logos-grid">
              {CLIENT_LOGO_FILES_HOME.map((file) => (
                <div key={file} className="client-logo-wrapper">
                  <img
                    src={`/Images/clientele/${file}`}
                    alt=""
                    className="client-logo-img"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
              ))}
            </div>
          </div>
          <div style={{ textAlign: 'center', marginTop: '1.25rem' }}>
            <Link to={`${lang}/clientele`} className="home-link-arrow">
              {t('clientele.viewAll')} →
            </Link>
          </div>
        </div>
      </section>

      {/* What we sell */}
      <section className="home-section">
        <div className="container">
          <span className="home-eyebrow">{t('offer.eyebrow')}</span>
          <h2 className="home-h2">{t('offer.title')}</h2>
          <div className="home-offer">
            <article className="home-offer-card home-offer-card--split">
              <div className="home-offer-card__media">
                <div className="home-img-wrap">
                  <SectionImage src={OFFER_IMAGE_SOFTWARE} alt={t('offer.softwareImageAlt')} />
                </div>
              </div>
              <div className="home-offer-card__body">
                <h3>{t('offer.softwareTitle')}</h3>
                <p>{t('offer.softwareBody')}</p>
                <Link to={`${lang}/applications`} className="home-link-arrow">
                  {t('offer.softwareCta')} →
                </Link>
              </div>
            </article>
            <article className="home-offer-card home-offer-card--split">
              <div className="home-offer-card__media">
                <div className="home-img-wrap">
                  <SectionImage src={OFFER_IMAGE_AI} alt={t('offer.aiImageAlt')} />
                </div>
              </div>
              <div className="home-offer-card__body">
                <h3>{t('offer.aiTitle')}</h3>
                <p>{t('offer.aiBody')}</p>
                <Link to={`${lang}/platform`} className="home-link-arrow">
                  {t('offer.aiCta')} →
                </Link>
              </div>
            </article>
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="home-section home-section--muted">
        <div className="container">
          <div className="home-section-head">
            <div>
              <span className="home-eyebrow">{t('industries.eyebrow')}</span>
              <h2 className="home-h2">{t('industries.title')}</h2>
            </div>
            <Link to={`${lang}/industries`} className="home-link-arrow" style={{ marginTop: 0 }}>
              {t('industries.viewAll')} →
            </Link>
          </div>
          <div className="home-industry-grid">
            {INDUSTRY_TILES.map((tile) => (
              <Link
                key={tile.id}
                className="home-industry-tile"
                to={`${lang}/${tile.path}`}
              >
                <div
                  className="home-industry-tile-bg"
                  style={{ backgroundImage: `url(${tile.image})` }}
                />
                <span>{tInd(`cards.${tile.id}.title`)}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* E1 services */}
      <section className="home-section">
        <div className="container">
          <span className="home-eyebrow">{t('services.eyebrow')}</span>
          <h2 className="home-h2" style={{ maxWidth: '42rem' }}>
            {t('services.sectionTitle')}
          </h2>
        </div>

        <div className="container" style={{ marginTop: 0 }}>
          <div className="home-service">
            <div className="home-img-wrap">
              <SectionImage src={SERVICE_IMAGES.knowledgeHub} alt={t('services.knowledgeHub.imageAlt')} />
            </div>
            <div>
              <h3 className="home-h2" style={{ fontSize: '1.35rem' }}>{t('services.knowledgeHub.title')}</h3>
              <p className="home-lead">{t('services.knowledgeHub.body')}</p>
              <Link to={`${lang}/knowledge-hub`} className="home-link-arrow">
                {t('services.knowledgeHub.cta')} →
              </Link>
            </div>
          </div>

          <div className="home-service home-service--reverse" style={{ marginTop: '2.5rem' }}>
            <div className="home-img-wrap">
              <SectionImage src={SERVICE_IMAGES.dataBridge} alt={t('services.dataBridge.imageAlt')} />
            </div>
            <div>
              <h3 className="home-h2" style={{ fontSize: '1.35rem' }}>{t('services.dataBridge.title')}</h3>
              <p className="home-lead">{t('services.dataBridge.body')}</p>
              <Link to={`${lang}/e1-databridge`} className="home-link-arrow">
                {t('services.dataBridge.cta')} →
              </Link>
            </div>
          </div>

          <div className="home-service" style={{ marginTop: '2.5rem' }}>
            <div className="home-img-wrap">
              <SectionImage src={SERVICE_IMAGES.investigation} alt={t('services.investigation.imageAlt')} />
            </div>
            <div>
              <h3 className="home-h2" style={{ fontSize: '1.35rem' }}>{t('services.investigation.title')}</h3>
              <p className="home-lead">{t('services.investigation.body')}</p>
              <Link to={`${lang}/e1-analytics`} className="home-link-arrow">
                {t('services.investigation.cta')} →
              </Link>
            </div>
          </div>
        </div>
      </section>


      {/* News */}
      <section className="home-section">
        <div className="container">
          <div className="home-section-head">
            <div>
              <span className="home-eyebrow">{t('news.eyebrow')}</span>
              <h2 className="home-h2">{t('news.title')}</h2>
            </div>
            <Link to={`${lang}/ai-news`} className="home-link-arrow" style={{ marginTop: 0 }}>
              {t('news.viewAll')} →
            </Link>
          </div>
          <div className="home-news-row">
            {newsItems.map((article) => (
              <Link
                key={article.slug}
                to={`${lang}/ai-news/${article.slug}`}
                className="home-news-card"
              >
                {article.cardImage && (
                  <div className="home-news-card-media">
                    <img
                      src={article.cardImage}
                      alt={article.cardImageAlt || ''}
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                )}
                <div className="home-news-card-body">
                  <div className="home-news-meta">{article.displayDate} · {article.category}</div>
                  <h3 className="home-news-title">{article.title}</h3>
                  <div className="home-news-excerpt-wrap">
                    <p className="home-news-excerpt">{article.excerpt}</p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Why Ewands */}
      {/* <section className="home-section home-section--muted">
        <div className="container">
          <span className="home-eyebrow">{t('why.eyebrow')}</span>
          <h2 className="home-h2">{t('why.title')}</h2>
          <div className="home-why" style={{ marginTop: '1rem' }}>
            <div className="home-img-wrap">
              <SectionImage src={WHY_IMAGE} alt={t('why.imageAlt')} />
            </div>
            <div className="home-why-points">
              {Array.isArray(whyItems) &&
                whyItems.map((item, idx) => (
                  <div key={item.title || `why-${idx}`} className="home-why-item">
                    <h3>{item.title}</h3>
                    <p>{item.text}</p>
                  </div>
                ))}
            </div>
          </div>
        </div>
      </section> */}

      {/* Closing CTA */}
      <section className="home-closing">
        <h2>{t('closingCta.title')}</h2>
        <div className="home-closing-cta">
          <Link to={`${lang}/contact`} className="hero-cta">
            {t('closingCta.primary')}
            <span className="cta-chevrons">
              <span>›</span>
              <span>›</span>
              <span>›</span>
            </span>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
